import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';
import { useUserSession } from '../../models/hooks/appStateHooks';

export const BASE_QUERY_KEY = `emailExecAssistant/templates`;

export const useEmailExecAssistantTemplatesQuery = ({
	enabled,
	meta,
}: {
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.EmailExecAssistantTemplate[]>('emailExecAssistant/templates', 'GET'),
		queryKey: [BASE_QUERY_KEY],
		meta,
	});
};

export function useEmailExecAssistantTemplatesSuspenseQuery({
	staleTime,
	meta,
}: {
	staleTime?: number;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useSuspenseQuery({
		queryKey: [BASE_QUERY_KEY],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.EmailExecAssistantTemplate[]>('emailExecAssistant/templates', 'GET'),
		staleTime,
		meta,
	});
}

export const invalidateEmailExecTemplates = () => reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });

export const selectBoundEmailExecTemplate = (
	templates: Api.EmailExecAssistantTemplate[]
): Api.EmailExecAssistantTemplate | undefined => {
	if (templates.length <= 1) {
		return templates[0];
	}
	return [...templates].sort((a, b) => (b.lastModifiedDate ?? '').localeCompare(a.lastModifiedDate ?? ''))[0];
};

export const updateEmailExecAssistantTemplatesRQCache = (updatedTemplate: Api.EmailExecAssistantTemplate) => {
	reactQueryClient.setQueryData<Api.EmailExecAssistantTemplate[]>([BASE_QUERY_KEY], oldTemplates => {
		if (!oldTemplates) {
			return [updatedTemplate];
		}
		const index = oldTemplates.findIndex(t => t.id === updatedTemplate.id);
		if (index === -1) {
			return [...oldTemplates, updatedTemplate];
		}
		const newTemplates = [...oldTemplates];
		newTemplates[index] = updatedTemplate;
		return newTemplates;
	});
};
